import React from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Heading,
  Text,
  VStack,
  Icon,
  useColorMode,
  Image,
  Badge,
  HStack,
  Tag,
  useBreakpointValue,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  FiMessageSquare,
  FiZap,
  FiVideo,
  FiClock,
  FiUsers,
  FiTool,
  FiShoppingCart,
  FiPackage,
  FiTrendingUp,
  FiGift,
  FiBarChart,
  FiSmartphone,
} from "react-icons/fi";
import { RiRobotLine, RiCustomerService2Line } from "react-icons/ri";
import { keyframes } from "@emotion/react";
import { TypeAnimation } from "react-type-animation";
import { PublicNavbar } from "./layout/PublicNavbar";
import { Footer } from "./layout/Footer";
const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const glowAnimation = keyframes`
  0% {
    opacity: 0.5;
    transform: translate(-50%, -50%) scale(0.8);
  }
  50% {
    opacity: 0.8;
    transform: translate(-50%, -50%) scale(1.2);
  }
  100% {
    opacity: 0.5;
    transform: translate(-50%, -50%) scale(0.8);
  }
`;

const FeatureCard: React.FC<{
  icon: React.ElementType;
  title: string;
  description: string;
}> = ({ icon, title, description }) => {
  const { colorMode } = useColorMode();

  return (
    <Box
      p={6}
      borderRadius="xl"
      border="1px"
      borderColor={colorMode === "dark" ? "gray.700" : "gray.200"}
      _hover={{
        transform: "translateY(-5px)",
        boxShadow: "xl",
      }}
      transition="all 0.3s">
      <VStack align="start" spacing={4}>
        <HStack>
          <Icon
            as={icon}
            w={6}
            h={6}
            color={colorMode === "dark" ? "brand.primary" : "brand.secondary"}
          />
          <Heading size="md" fontFamily="Nasalization">
            {title}
          </Heading>
        </HStack>
        <Text color={colorMode === "dark" ? "gray.400" : "gray.600"}>
          {description}
        </Text>
      </VStack>
    </Box>
  );
};

export const Nuvemshop: React.FC = () => {
  const { colorMode } = useColorMode();
  const { t } = useTranslation();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const mainFeatures = [
    {
      icon: RiCustomerService2Line,
      title: t(
        "nuvemshop.features.support24.title",
        "Atendimento Automatizado 24/7"
      ),
      description: t(
        "nuvemshop.features.support24.description",
        "Ofereça suporte instantâneo aos seus clientes com agentes de IA personalizados que funcionam no seu site e via WhatsApp, mesmo quando você não está disponível."
      ),
    },
    {
      icon: RiRobotLine,
      title: t("nuvemshop.features.automation.title", "Automação Inteligente"),
      description: t(
        "nuvemshop.features.automation.description",
        "Reduza horas de trabalho manual com a assistente virtual Ária, que acessa dados, cria produtos e gerencia promoções diretamente do painel."
      ),
    },
    {
      icon: FiVideo,
      title: t(
        "nuvemshop.features.marketing.title",
        "Marketing Visual Poderoso"
      ),
      description: t(
        "nuvemshop.features.marketing.description",
        "Crie vídeos promocionais profissionais em minutos com nossa ferramenta de geração de vídeos por IA, sem necessidade de equipe especializada."
      ),
    },
    {
      icon: FiZap,
      title: t("nuvemshop.features.integration.title", "Integração Perfeita"),
      description: t(
        "nuvemshop.features.integration.description",
        "Todas as ferramentas de IA da Araria se integram nativamente à sua loja Nuvem Shop, sem necessidade de configurações técnicas complexas."
      ),
    },
  ];

  const agentFeatures = [
    {
      icon: FiPackage,
      title: t(
        "nuvemshop.agent.features.knowledge.title",
        "Base de Conhecimento Personalizada"
      ),
      description: t(
        "nuvemshop.agent.features.knowledge.description",
        "Alimente seu agente com informações específicas sobre seus produtos, políticas de troca, prazos de entrega e FAQs"
      ),
    },
    {
      icon: FiMessageSquare,
      title: t(
        "nuvemshop.agent.features.channels.title",
        "Múltiplos Canais de Atendimento"
      ),
      description: t(
        "nuvemshop.agent.features.channels.description",
        "Disponibilize o mesmo agente no seu site e via WhatsApp"
      ),
    },
    {
      icon: FiUsers,
      title: t(
        "nuvemshop.agent.features.personality.title",
        "Personalidade Ajustável"
      ),
      description: t(
        "nuvemshop.agent.features.personality.description",
        "Configure o tom de comunicação do seu agente para refletir a identidade da sua marca"
      ),
    },
    {
      icon: FiBarChart,
      title: t(
        "nuvemshop.agent.features.analytics.title",
        "Análise de Performance"
      ),
      description: t(
        "nuvemshop.agent.features.analytics.description",
        "Acompanhe métricas de atendimento e satisfação dos clientes"
      ),
    },
  ];

  const whatsappFeatures = [
    {
      icon: FiClock,
      title: t(
        "nuvemshop.whatsapp.features.instant.title",
        "Atendimento Instantâneo"
      ),
      description: t(
        "nuvemshop.whatsapp.features.instant.description",
        "Respostas imediatas 24/7 no canal preferido dos consumidores"
      ),
    },
    {
      icon: FiShoppingCart,
      title: t(
        "nuvemshop.whatsapp.features.recovery.title",
        "Recuperação de Carrinhos"
      ),
      description: t(
        "nuvemshop.whatsapp.features.recovery.description",
        "Lembre clientes sobre produtos abandonados no carrinho"
      ),
    },
    {
      icon: FiGift,
      title: t(
        "nuvemshop.whatsapp.features.campaigns.title",
        "Campanhas Promocionais"
      ),
      description: t(
        "nuvemshop.whatsapp.features.campaigns.description",
        "Envie ofertas personalizadas para clientes segmentados"
      ),
    },
    {
      icon: FiTrendingUp,
      title: t(
        "nuvemshop.whatsapp.features.tracking.title",
        "Acompanhamento de Pedidos"
      ),
      description: t(
        "nuvemshop.whatsapp.features.tracking.description",
        "Atualize clientes sobre status de entrega automaticamente"
      ),
    },
  ];

  const items = (t("nuvemshop.typeAnimationFeatures", {
    returnObjects: true,
  }) as { title: string }[]) || [
    { title: "Atendimento com IA 24/7, suporte WhatsApp" },
    { title: "Automação Inteligente" },
    // { title: "Marketing Visual" },
    { title: "WhatsApp Business" },
  ];

  const sequence = Array.isArray(items)
    ? items.flatMap((item): (string | number)[] => [item.title, 3000])
    : [];
  const [isOpen, setIsOpen] = React.useState(false);
  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  return (
    <Box>
      <PublicNavbar />

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent
          bg={useColorModeValue("white", "gray.800")}
          borderColor={useColorModeValue("gray.200", "gray.700")}>
          <ModalHeader fontFamily="Nasalization">
            {t("chatbot.modal.title", "Tutorial")}{" "}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Box>
              <iframe
                width="100%"
                height="215"
                src="https://www.youtube.com/embed/NiryMZNFpFY?si=q0izw9ZrUYoSMU0M"
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                // referrerpolicy="strict-origin-when-cross-origin"
                // allowfullscreen
              ></iframe>
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>
              {t("common.close", "Fechar")}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                window.open(
                  "https://blog.araria.com.br/araria-na-nuvemshop/",
                  "_blank"
                );
              }}>
              {t("common.knowMore", "Saber Mais")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Hero Section */}
      <Box
        position="relative"
        overflow="hidden"
        py={40}
        borderBottom="1px"
        borderColor={colorMode === "dark" ? "gray.700" : "gray.200"}>
        {/* Animated background */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bgGradient={
            colorMode === "dark"
              ? "linear(to-r, gray.900, brand.secondary, gray.900)"
              : "linear(to-r, white, brand.primary, white)"
          }
          backgroundSize="400% 400%"
          animation={`${gradientAnimation} 15s ease infinite`}
          opacity="0.1"
        />

        {/* Glowing orbs */}
        <Box
          position="absolute"
          top="90%"
          left="50%"
          width="800px"
          height="800px"
          borderRadius="full"
          bg={colorMode === "dark" ? "brand.secondary" : "white"}
          filter="blur(80px)"
          opacity="0.15"
          animation={`${glowAnimation} 7s ease-in-out infinite`}
          transform="translate(-50%, -50%)"
        />

        {/* Content */}
        <Container maxW="container.lg" position="relative">
          <VStack spacing={12} textAlign="center">
            <VStack spacing={8}>
              <Heading
                size="2xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text">
                {t(
                  "nuvemshop.hero.title",
                  "Suíte de Inteligência Artificial Nuvem Shop"
                )}
              </Heading>
              <Box maxW="2xl" minH="60px">
                <TypeAnimation
                  sequence={sequence}
                  wrapper="span"
                  speed={89}
                  style={{
                    fontSize: "1.25rem",
                    display: "inline-block",
                    fontFamily: "Nasalization",
                    color: useColorModeValue("#00392b", "#a0aec0"),
                  }}
                  repeat={Infinity}
                />
              </Box>
              <Text
                fontSize="xl"
                color={colorMode === "dark" ? "gray.400" : "gray.600"}
                maxW="2xl">
                {t(
                  "nuvemshop.hero.subtitle",
                  "O Hub de IA da Araria traz o poder da inteligência artificial para sua loja Nuvem Shop, com agentes inteligentes de atendimento, automação e ferramentas de marketing avançadas."
                )}
              </Text>
              <HStack spacing={4}>
                <Button
                  size="lg"
                  bg="brand.primary"
                  color="black"
                  _hover={{
                    bg: "brand.secondary",
                    color: "white",
                  }}
                  onClick={() =>
                    (window.location.href =
                      "https://www.nuvemshop.com.br/apps/15780/authorize")
                  }
                  fontFamily="Nasalization">
                  {t("nuvemshop.hero.cta.primary", "Instalar Agora")}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  borderColor="brand.primary"
                  color={
                    colorMode === "dark" ? "brand.primary" : "brand.secondary"
                  }
                  _hover={{
                    bg: "brand.primary",
                    color: "black",
                  }}
                  onClick={() => {
                    /* Add video modal or demo link */
                    // window.open(
                    //   "https://blog.araria.com.br/araria-na-nuvemshop/",
                    //   "_blank"
                    // );
                    onOpen();
                  }}
                  fontFamily="Nasalization">
                  {t("nuvemshop.hero.cta.secondary", "Veja como funciona")}
                </Button>
              </HStack>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* Main Features Section */}
      <Box py={20} bg={colorMode === "dark" ? "gray.800" : "gray.100"}>
        <Container maxW="container.lg">
          <VStack spacing={16}>
            <VStack spacing={4} textAlign="center">
              <Heading
                size="xl"
                fontFamily="Nasalization"
                color={
                  colorMode === "dark" ? "brand.primary" : "brand.secondary"
                }>
                {t(
                  "nuvemshop.mainFeatures.title",
                  "Transforme sua Operação de E-commerce"
                )}
              </Heading>
              <Text
                fontSize="lg"
                color={colorMode === "dark" ? "gray.400" : "gray.600"}
                maxW="2xl">
                {t(
                  "nuvemshop.mainFeatures.subtitle",
                  "Potencialize suas vendas com recursos avançados de IA"
                )}
              </Text>
            </VStack>

            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(2, 1fr)",
              }}
              gap={8}
              w="full">
              {mainFeatures.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* AI Agents Section */}
      <Box py={20} bg={colorMode === "dark" ? "gray.900" : "white"}>
        <Container maxW="container.lg">
          <VStack spacing={16}>
            <VStack spacing={4} textAlign="center">
              <Heading
                size="xl"
                fontFamily="Nasalization"
                color={
                  colorMode === "dark" ? "brand.primary" : "brand.secondary"
                }>
                {t(
                  "nuvemshop.agents.title",
                  "Agentes de IA: Atendimento Inteligente para sua Loja"
                )}
              </Heading>
              <Text
                fontSize="lg"
                color={colorMode === "dark" ? "gray.400" : "gray.600"}
                maxW="2xl">
                {t(
                  "nuvemshop.agents.subtitle",
                  "Transforme o atendimento ao cliente da sua loja com agentes de IA personalizados que conhecem seus produtos, políticas e respondem dúvidas 24 horas por dia, 7 dias por semana."
                )}
              </Text>
            </VStack>

            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(2, 1fr)",
              }}
              gap={8}
              w="full">
              {agentFeatures.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* WhatsApp Section */}
      <Box py={20} bg={colorMode === "dark" ? "gray.800" : "gray.100"}>
        <Container maxW="container.lg">
          <VStack spacing={16}>
            <VStack spacing={4} textAlign="center">
              <Heading
                size="xl"
                fontFamily="Nasalization"
                color={
                  colorMode === "dark" ? "brand.primary" : "brand.secondary"
                }>
                {t(
                  "nuvemshop.whatsapp.title",
                  "Atendimento via WhatsApp com Inteligência Artificial"
                )}
              </Heading>
              <Text
                fontSize="lg"
                color={colorMode === "dark" ? "gray.400" : "gray.600"}
                maxW="2xl">
                {t(
                  "nuvemshop.whatsapp.subtitle",
                  "Leve seu atendimento para onde seus clientes estão. Os agentes da Araria funcionam perfeitamente no WhatsApp, permitindo que você automatize tanto campanhas promocionais quanto suporte ao cliente."
                )}
              </Text>
            </VStack>

            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(2, 1fr)",
              }}
              gap={8}
              w="full">
              {whatsappFeatures.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Aria Assistant Section */}
      <Box py={20} bg={colorMode === "dark" ? "gray.900" : "white"}>
        <Container maxW="container.lg">
          <VStack spacing={16}>
            <VStack spacing={4} textAlign="center">
              <Heading
                size="xl"
                fontFamily="Nasalization"
                color={
                  colorMode === "dark" ? "brand.primary" : "brand.secondary"
                }>
                {t(
                  "nuvemshop.aria.title",
                  "Conheça Ária: Sua Assistente Virtual Especializada em E-commerce"
                )}
              </Heading>
              <Text
                fontSize="lg"
                color={colorMode === "dark" ? "gray.400" : "gray.600"}
                maxW="2xl">
                {t(
                  "nuvemshop.aria.subtitle",
                  "Ária não é um simples chatbot. É uma assistente inteligente desenvolvida especificamente para gestão de e-commerce, integrada ao ambiente administrativo da sua loja Nuvem Shop."
                )}
              </Text>
            </VStack>

            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              }}
              gap={8}
              w="full">
              {[
                {
                  icon: FiPackage,
                  title: t(
                    "nuvemshop.aria.features.products.title",
                    "Gestão de Produtos"
                  ),
                  description: t(
                    "nuvemshop.aria.features.products.description",
                    "Crie e atualize produtos com assistência inteligente"
                  ),
                },
                {
                  icon: FiGift,
                  title: t(
                    "nuvemshop.aria.features.promotions.title",
                    "Gestão de Promoções"
                  ),
                  description: t(
                    "nuvemshop.aria.features.promotions.description",
                    "Gerencie promoções e cupons de desconto facilmente"
                  ),
                },
                {
                  icon: FiTool,
                  title: t(
                    "nuvemshop.aria.features.tools.title",
                    "Ferramentas Avançadas"
                  ),
                  description: t(
                    "nuvemshop.aria.features.tools.description",
                    "Acesse ferramentas de otimização e análise da loja"
                  ),
                },
              ].map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Video Creator Section */}
      <Box py={20} bg={colorMode === "dark" ? "gray.800" : "gray.100"}>
        <Container maxW="container.lg">
          <VStack spacing={16}>
            <VStack spacing={4} textAlign="center">
              <Heading
                size="xl"
                fontFamily="Nasalization"
                color={
                  colorMode === "dark" ? "brand.primary" : "brand.secondary"
                }>
                {t(
                  "nuvemshop.videoCreator.title",
                  "Transforme Fotos em Vídeos Profissionais em Minutos"
                )}
              </Heading>
              <Text
                fontSize="lg"
                color={colorMode === "dark" ? "gray.400" : "gray.600"}
                maxW="2xl">
                {t(
                  "nuvemshop.videoCreator.subtitle",
                  "Nossa ferramenta de criação de vídeos com IA transforma as fotos do seu catálogo em conteúdo publicitário profissional para suas redes sociais e campanhas de marketing."
                )}
              </Text>
            </VStack>

            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              }}
              gap={8}
              w="full">
              {[
                {
                  icon: FiVideo,
                  title: t(
                    "nuvemshop.videoCreator.features.creation.title",
                    "Criação Automática"
                  ),
                  description: t(
                    "nuvemshop.videoCreator.features.creation.description",
                    "Transforme fotos em vídeos profissionais automaticamente"
                  ),
                },
                {
                  icon: FiTool,
                  title: t(
                    "nuvemshop.videoCreator.features.customization.title",
                    "Personalização Total"
                  ),
                  description: t(
                    "nuvemshop.videoCreator.features.customization.description",
                    "Adicione sua marca, música e elementos visuais"
                  ),
                },
                {
                  icon: FiSmartphone,
                  title: t(
                    "nuvemshop.videoCreator.features.sharing.title",
                    "Compartilhamento Fácil"
                  ),
                  description: t(
                    "nuvemshop.videoCreator.features.sharing.description",
                    "Publique diretamente nas redes sociais"
                  ),
                },
              ].map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box py={20} bg={colorMode === "dark" ? "gray.900" : "white"}>
        <Container maxW="container.lg">
          <VStack spacing={8} textAlign="center">
            <Heading
              size="xl"
              fontFamily="Nasalization"
              bgGradient="linear(to-r, brand.primary, brand.secondary)"
              bgClip="text">
              {t(
                "nuvemshop.cta.title",
                "Comece a Transformar seu E-commerce Hoje"
              )}
            </Heading>
            <Text
              fontSize="lg"
              color={colorMode === "dark" ? "gray.400" : "gray.600"}
              maxW="2xl">
              {t(
                "nuvemshop.cta.subtitle",
                "Instale o Hub de IA da Araria e leve sua loja Nuvem Shop para o próximo nível"
              )}
            </Text>
            <Button
              size="lg"
              bg="brand.primary"
              color="black"
              _hover={{
                bg: "brand.secondary",
                color: "white",
              }}
              onClick={() =>
                (window.location.href =
                  "https://www.nuvemshop.com.br/apps/15780/authorize")
              }
              fontFamily="Nasalization">
              {t("nuvemshop.cta.button", "Instalar Gratuitamente")}
            </Button>
          </VStack>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};
